// Models
import { Temporal } from "temporal-polyfill-lite";
import { CustomFieldValueType } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
/**
 * The custom field value entity implementation.
 */ export class CustomFieldValue extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        if (changed.value === undefined) {
            return {};
        }
        return {
            value: this.convertValueToValueResource(changed.value)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            // valueType is not expected to change, but if it does, we want to use the new valueType to convert the value in case the format has changed
            value: (value)=>CustomFieldValue.convertValueResourceToValue(delta.valueType ?? this.valueType, value)
        });
    }
    static fromResource(resource) {
        return new CustomFieldValue({
            id: resource.id,
            version: resource["@etag"],
            customFieldId: resource.customFieldId,
            valueType: resource.valueType,
            value: this.convertValueResourceToValue(resource.valueType, resource.value)
        });
    }
    getClone() {
        return new CustomFieldValue({
            id: this.id,
            version: this.version,
            customFieldId: this.customFieldId,
            valueType: this.valueType,
            value: this.value
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            customFieldId: this.customFieldId,
            valueType: this.valueType,
            value: this.convertValueToValueResource(this.value)
        };
    }
    forCreate() {
        return {
            customFieldId: this.customFieldId,
            valueType: this.valueType,
            value: this.convertValueToValueResource(this.value)
        };
    }
    /**
     * Converts the value to a string for serialization in the resource, based on the valueType. For example, if the valueType is Date, it converts the Temporal.PlainDate to an ISO string.
     * @param value The value to convert
     */ convertValueToValueResource(value) {
        switch(this.valueType){
            case CustomFieldValueType.Date:
                return value.toString();
            case CustomFieldValueType.String:
                return value;
            case CustomFieldValueType.Integer:
                return value.toString();
            case CustomFieldValueType.Flag:
                return value.toString();
            case CustomFieldValueType.SingleChoiceText:
            default:
                return value;
        }
    }
    /**
     * Converts the value from the resource to the appropriate type based on the valueType. For example, if the valueType is Date, it converts the ISO string to a Temporal.PlainDate.
     * @param valueType The type of the value
     * @param value The value from the resource to convert
     */ static convertValueResourceToValue(valueType, value) {
        switch(valueType){
            case CustomFieldValueType.Date:
                return Temporal.PlainDate.from(value);
            case CustomFieldValueType.String:
                return value;
            case CustomFieldValueType.Integer:
                return Number(value);
            case CustomFieldValueType.Flag:
                return value === "true";
            case CustomFieldValueType.SingleChoiceText:
            default:
                return value;
        }
    }
    constructor(propertyBag){
        super(propertyBag);
        this.customFieldId = propertyBag.customFieldId;
        this.valueType = propertyBag.valueType;
        this.value = propertyBag.value;
    }
}
